/**
 * The tender's own contract-term series: which month count each Year N *is*.
 *
 * ⚠️ WHY THIS EXISTS — THE APP DID NOT KNOW WHAT THE TENDER ASKED FOR.
 *
 * `sys_config.requested_contract_terms` is a single ORG-WIDE row, `{terms:[12,24,36,48,60,72]}`.
 * A tender that asked retailers for 4/16/28/40 months had nowhere to say so, and every term
 * was labelled by snapping to that global list and dividing by 12. Measured on the generated
 * document for dev tender 7a96958d (EXCH PTY LTD, requested 4/16/28/40):
 *
 *   yearsForTerm(4)  -> snaps to 12 -> Year 1
 *   yearsForTerm(16) -> snaps to 12 -> Year 1   <-- collides with the 4-month term
 *   yearsForTerm(28) -> snaps to 24 -> Year 2
 *   yearsForTerm(40) -> snaps to 36 -> Year 3
 *
 * The consequences were all silent. The "Year 1" page was keyed to term 12 and printed AGL's
 * 16-MONTH rates (8.8653/5.5631) because 16 snaps into the 12 bucket. The 40-month figures
 * appeared on BOTH the Year 3 and Year 4 pages. And AGL's and Origin's real 4-month quotes
 * never appeared anywhere, because the 12-month term claimed Year 1 first.
 *
 * ⚠️ THE YEAR IS THE TERM'S POSITION IN THE SERIES, NOT months / 12.
 *
 * That is the whole idea, and it is not an invention — it is how the client's own workbook
 * works. Their Data Input Page ends with an index -> months table:
 *
 *   1  0.331506849315068   4       (121/365)
 *   2  1.32876712328767    16      (485/365)
 *   3  2.33150684931507    28      (851/365)
 *   4  3.33150684931507     40     (1216/365)
 *
 * One table for the whole tender. Positions are unique by construction, so 4 and 16 cannot
 * collide, no year can be claimed twice, and no page can borrow another term's data.
 *
 * Standard tenders are unaffected: a 12/24/36/48 series puts those terms at positions
 * 1/2/3/4, which is exactly what `months / 12` already produced.
 *
 * Stored on `tender.extn.term_series` — jsonb, so no schema change.
 */

import type { TermYearMapping } from './termLabel';

/** `{ "1": 4, "2": 16, "3": 28, "4": 40 }` — year number -> that year's month count. */
export type TenderTermSeries = Record<string, number | string>;

/**
 * How far an offer's own term may sit from a series entry and still be that year.
 *
 * Retailers answering one request do not all round the same way — WPG's four retailers
 * answered a 16-month request with 16, 16, 16 and 15 months. Two months is wide enough for
 * that and narrow enough that a genuine 12-month product is never mistaken for a 16-month one.
 */
export const SERIES_TOLERANCE_MONTHS = 2;

const isPositiveInt = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0 && Number.isInteger(n);

/**
 * The series as `year -> months`, with junk dropped.
 *
 * Accepts string keys and string values because it arrives from jsonb and from `<input>`
 * elements, where a typed number is a string until it is parsed.
 */
export const normaliseTermSeries = (series?: TenderTermSeries | null): Map<number, number> => {
  const out = new Map<number, number>();
  if (!series || typeof series !== 'object') return out;

  for (const [rawYear, rawMonths] of Object.entries(series)) {
    const year = typeof rawYear === 'number' ? rawYear : parseInt(String(rawYear), 10);
    const months = typeof rawMonths === 'number' ? rawMonths : parseInt(String(rawMonths), 10);
    if (!isPositiveInt(year) || !isPositiveInt(months)) continue;
    out.set(year, months);
  }
  return out;
};

/**
 * The series as a plain object fit to store — the shape `normaliseTermSeries` would read back.
 *
 * ⚠️ THE ROW WRITTEN WAS NOT THE ROW READ.
 *
 * The panel's year inputs hold `''` while a value is being retyped, and closing the popover
 * mid-edit committed that `''` straight into `tender.extn.term_series`. Every reader drops it
 * again, so the database showed a year that no screen and no calculation would honour — a
 * silent hole in the middle of a series, indistinguishable from a year nobody had set. Written
 * through this, what lands in jsonb is exactly what comes back out.
 */
export const cleanTermSeries = (series?: TenderTermSeries | null): TenderTermSeries => {
  const out: TenderTermSeries = {};
  for (const [year, months] of normaliseTermSeries(series)) out[String(year)] = months;
  return out;
};

/** Years in the series, ascending. */
export const seriesYears = (series?: TenderTermSeries | null): number[] =>
  [...normaliseTermSeries(series).keys()].sort((a, b) => a - b);

/** The month count the series says Year N is, or null. */
export const seriesMonthsForYear = (
  year: number,
  series?: TenderTermSeries | null,
): number | null => normaliseTermSeries(series).get(year) ?? null;

/**
 * The year a term is, according to the series.
 *
 * An exact month match wins. Failing that the nearest entry within SERIES_TOLERANCE_MONTHS
 * takes it, so a retailer answering the 16-month request with 15 months still lands on Year 2.
 * Returns null when the series does not describe this term at all — the caller then falls back
 * to the derived behaviour, so an unconfigured tender behaves exactly as it does today.
 */
export const termYearFromSeries = (
  months: number,
  series?: TenderTermSeries | null,
): number | null => {
  const m = typeof months === 'number' ? months : parseInt(String(months), 10);
  if (!Number.isFinite(m) || m <= 0) return null;

  const normalised = normaliseTermSeries(series);
  if (normalised.size === 0) return null;

  let best: { year: number; distance: number } | null = null;
  for (const [year, seriesMonths] of normalised) {
    if (seriesMonths === m) return year;                       // exact wins outright
    const distance = Math.abs(seriesMonths - m);
    if (distance > SERIES_TOLERANCE_MONTHS) continue;
    // Ties go to the LOWER year: a term equidistant between two entries belongs to the
    // shorter one, which can only ever under-state rather than over-state the contract.
    if (!best || distance < best.distance || (distance === best.distance && year < best.year)) {
      best = { year, distance };
    }
  }
  return best?.year ?? null;
};

/**
 * The series expressed as a term -> year mapping, so it can be merged with the reviewer's own
 * per-offer assignments by the existing mergeTermYearMappings (first wins, so a per-offer
 * assignment passed BEFORE this one still overrides the series).
 */
export const seriesToTermYearMapping = (series?: TenderTermSeries | null): TermYearMapping => {
  const mapping: TermYearMapping = {};
  for (const [year, months] of normaliseTermSeries(series)) {
    // Year wins the LOWEST month count that names it, so a malformed series with two entries
    // for one year cannot produce two different answers for the same term.
    if (mapping[String(months)] === undefined) mapping[String(months)] = year;
  }
  return mapping;
};

/**
 * The series to propose after parsing: the term set the MOST offers agree on.
 *
 * ⚠️ THE MODAL SET, NOT THE UNION.
 *
 * Dev tender 7a96958d holds two incompatible series at once — AGL, Origin, Flo and Next
 * Business quote 4/16/28/40/52 while ALINTA, Engie and Momentum quote 12/24/36/48 because
 * nobody re-termed them. The union of those is 4/12/16/24/28/36/40/48/52, which describes no
 * real tender. Proposing the set the plurality of offers share recovers what was actually
 * asked for, and the offers that disagree are exactly the ones worth warning about.
 *
 * Ties break toward the LONGER set, so a tender whose offers split evenly still gets every
 * year a page rather than silently dropping the tail.
 */
export const proposeTermSeries = (
  offerTermLists: Array<number[] | null | undefined>,
): TenderTermSeries => {
  const tally = new Map<string, { terms: number[]; count: number }>();

  for (const list of offerTermLists) {
    if (!Array.isArray(list)) continue;
    const terms = [...new Set(list.filter(isPositiveInt))].sort((a, b) => a - b);
    if (terms.length === 0) continue;
    const key = terms.join(',');
    const seen = tally.get(key);
    if (seen) seen.count += 1;
    else tally.set(key, { terms, count: 1 });
  }
  if (tally.size === 0) return {};

  let winner: { terms: number[]; count: number } | null = null;
  for (const candidate of tally.values()) {
    if (!winner
      || candidate.count > winner.count
      || (candidate.count === winner.count && candidate.terms.length > winner.terms.length)) {
      winner = candidate;
    }
  }
  if (!winner) return {};

  /**
   * ⚠️ A YEAR IS TWELVE MONTHS ON FROM THE ONE BEFORE IT. A TAIL IS NOT A YEAR.
   *
   * The winning list used to be numbered 1..N verbatim. Engie answered one tender with
   * 12 / 24 / 36 / 48 / 51 — four years and then a 51-month option running to a calendar year
   * end — and "Use suggested" made 51 a fifth YEAR, three months after the fourth. Every offer
   * was then measured against a series with a broken step, and the panel asked to re-term terms
   * that were already right.
   *
   * Year 1 may be any length: 3, 4, 10, 15 and 16 all occur. Every year AFTER it has to land
   * about twelve months past the last one KEPT, within the same tolerance the rest of the model
   * uses. A term that does not is an extra option, and the panel already has a place for those.
   */
  const series: TenderTermSeries = {};
  let year = 0;
  let previous: number | null = null;
  for (const months of winner.terms) {
    if (previous !== null && Math.abs(months - previous - 12) > SERIES_TOLERANCE_MONTHS) continue;
    series[String(++year)] = months;
    previous = months;
  }
  return series;
};

/**
 * Which of an offer's terms the series does not describe.
 *
 * This is what turns "you had to know to re-term ALINTA" into something the screen says. An
 * offer quoting 12/24/36 against a 4/16/28/40 series returns all three, and the reviewer is
 * told the offer will otherwise be compared against a different contract length.
 */
export const offerTermsOutsideSeries = (
  offerTerms: Array<number | string> | null | undefined,
  series?: TenderTermSeries | null,
): number[] => {
  if (!Array.isArray(offerTerms)) return [];
  if (normaliseTermSeries(series).size === 0) return [];

  const outside: number[] = [];
  for (const raw of offerTerms) {
    const months = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!isPositiveInt(months)) continue;
    if (termYearFromSeries(months, series) === null && !outside.includes(months)) {
      outside.push(months);
    }
  }
  return outside.sort((a, b) => a - b);
};

/**
 * True when the series is a plain run of whole years — 12/24/36/48 at positions 1/2/3/4.
 *
 * Used to prove the standard case is a no-op: for such a series the position IS months / 12,
 * so every figure a standard tender prints today is unchanged.
 */
export const isWholeYearSeries = (series?: TenderTermSeries | null): boolean => {
  const normalised = normaliseTermSeries(series);
  if (normalised.size === 0) return false;
  for (const [year, months] of normalised) {
    if (months !== year * 12) return false;
  }
  return true;
};

/**
 * Where a term the series does not describe actually sits relative to it.
 *
 * ⚠️ TWO VERY DIFFERENT PROBLEMS WERE BEING REPORTED IDENTICALLY.
 *
 * On WERRIBEE's 4/16/28/40 tender, two offers had a leftover term and the panel said the same
 * thing about both:
 *
 *   Flo quotes 52 months      — LONGER than the tender's longest year. The tender asked for four
 *                               years; a fifth option is simply extra. Nothing is wrong with the
 *                               offer, and there is nothing for the reviewer to do. Flo also has
 *                               no 4-month quote, so it starts at Year 2 — which is already how
 *                               its 16/28/40 are labelled, and matches the client's own sheet
 *                               leaving Flo's Year 1 blank.
 *
 *   Next Business quotes 24   — BETWEEN Year 2 (16 months) and Year 3 (28 months). This one is a
 *                               real hole: the retailer answered a request with a length the
 *                               tender never asked for, so that year of their offer is missing
 *                               from the comparison entirely unless someone decides which year it
 *                               was meant to be. No rule can decide it — 24 is 8 months from both.
 *
 * Calling both "has no year" trains the reviewer to ignore the message that actually needs them.
 */
export type OutsideTermPlacement =
  | { kind: 'beyond'; lastYear: number; lastMonths: number }
  | { kind: 'before'; firstYear: number; firstMonths: number }
  | {
      kind: 'between';
      lowerYear: number; lowerMonths: number;
      upperYear: number; upperMonths: number;
    };

/**
 * Classify a term the series has no year for. Returns null when the series DOES describe it
 * (or when there is no series), so a caller can use this as the single test.
 */
export const classifyOutsideTerm = (
  months: number,
  series?: TenderTermSeries | null,
): OutsideTermPlacement | null => {
  const m = typeof months === 'number' ? months : parseInt(String(months), 10);
  if (!isPositiveInt(m)) return null;

  const normalised = normaliseTermSeries(series);
  if (normalised.size === 0) return null;
  if (termYearFromSeries(m, series) !== null) return null;   // it fits — nothing to classify

  const years = [...normalised.keys()].sort((a, b) => a - b);
  const entries = years.map(year => ({ year, months: normalised.get(year)! }));
  // Sorted by MONTHS, not by year: a hand-edited series could number them out of order, and
  // "between" is a statement about lengths.
  const byMonths = [...entries].sort((a, b) => a.months - b.months);

  const shortest = byMonths[0];
  const longest = byMonths[byMonths.length - 1];

  if (m > longest.months) {
    return { kind: 'beyond', lastYear: longest.year, lastMonths: longest.months };
  }
  if (m < shortest.months) {
    return { kind: 'before', firstYear: shortest.year, firstMonths: shortest.months };
  }

  let lower = byMonths[0];
  let upper = byMonths[byMonths.length - 1];
  for (const entry of byMonths) {
    if (entry.months < m) lower = entry;
    if (entry.months > m) { upper = entry; break; }
  }
  return {
    kind: 'between',
    lowerYear: lower.year, lowerMonths: lower.months,
    upperYear: upper.year, upperMonths: upper.months,
  };
};
